import i18n from 'i18n/config';

/**
 * Feature names are column names on a measurement record. They are precise and
 * unreadable in equal measure, so the screen shows a plain-language name and
 * keeps the column name beside it - the column name is what support will be
 * asked about, and hiding it would cost more than it saves.
 */
const TOKENS = {
  rms: 'RMS',
  p2p: 'peak-to-peak',
  peak: 'peak',
  demod: 'demodulated',
  acceleration: 'acceleration',
  velocity: 'velocity',
  sound: 'sound',
  level: 'level',
  on: 'on',
  off: 'off',
  state: 'state',
  shaft: 'shaft',
  speed: 'speed'
};

const L = 'adminDashboard.alarmParameters.labels.';

// Per word, so word order stays English in every language.
export const featureLabel = (feature) => {
  if (!feature) return '';
  const words = feature.split('_').map(token => (
    TOKENS[token] ? i18n.t(`${L}tokens.${token}`, { defaultValue: TOKENS[token] }) : token
  ));
  const phrase = words.join(' ');

  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
};

// For use mid-sentence: "peak velocity", but "RMS velocity" keeps its acronym and
// German keeps its capitalised nouns.
export const midSentence = (label) => {
  if (!label || /^[A-Z]{2}/.test(label) || (i18n.language || '').indexOf('de') === 0) return label;

  return label.charAt(0).toLowerCase() + label.slice(1);
};

// A narrowband name repeats once per amplitude, so the pair is its identity.
export const featureOptionKey = item => (item.band === 'narrowband'
  ? `${item.feature}|${item.amplitude_type}`
  : item.feature);

export const featureOptionLabel = (item, word = label => label) => (item.band === 'narrowband'
  ? `${word(featureLabel(item.feature))} (${word(featureLabel(item.amplitude_type))})`
  : word(featureLabel(item.feature)));

// A measure the server says would clash stays visible, with the reason, but cannot be picked.
export const featureOptions = items => items.map(item => ({
  value: featureOptionKey(item),
  text: featureOptionLabel(item),
  disabled: Boolean(item.conflict),
  description: item.conflict || undefined
}));

export const createAlarmParameterBody = (tagType, item) => {
  const body = { tag_type: tagType, feature: item.feature };
  if (item.band === 'narrowband') body.amplitude_type = item.amplitude_type;

  return body;
};

// Only names that underscores-to-spaces cannot produce.
const TAG_TYPE_LABELS = {
  ultrasound_vector: 'Ultrasound (spectrum)'
};

export const tagTypeLabel = (tagType) => {
  if (!tagType) return '';

  const phrase = TAG_TYPE_LABELS[tagType] || tagType.replace(/_/g, ' ');

  return i18n.t(`${L}tagTypes.${tagType}`, {
    defaultValue: phrase.charAt(0).toUpperCase() + phrase.slice(1)
  });
};

/**
 * Vibration offers eight features that form two families - acceleration and
 * velocity - and the choice a person makes first is which family they want.
 * Sorted by identifier those families interleave, so they are ordered here:
 * amplitude first, then the statistic, with the demodulated variants last
 * because they are the specialist ones.
 *
 * Not a second dropdown. The two axes are not a full grid - there is no
 * demodulated velocity - so an amplitude selector would offer combinations
 * that do not exist and would need the feature list constrained by it.
 */
const AMPLITUDE_ORDER = ['acceleration', 'velocity'];

const STATISTIC_ORDER = ['rms', 'peak', 'p2p'];

const statisticRank = (feature) => {
  const stem = feature.split('_')[0];
  const rank = STATISTIC_ORDER.indexOf(stem);

  return (rank === -1 ? STATISTIC_ORDER.length : rank)
    + (feature.indexOf('demod') !== -1 ? STATISTIC_ORDER.length + 1 : 0);
};

export const byFamily = (a, b) => {
  const ampA = AMPLITUDE_ORDER.indexOf(a.amplitude_type);
  const ampB = AMPLITUDE_ORDER.indexOf(b.amplitude_type);

  if (ampA !== ampB) {
    return (ampA === -1 ? AMPLITUDE_ORDER.length : ampA)
      - (ampB === -1 ? AMPLITUDE_ORDER.length : ampB);
  }

  const rankDiff = statisticRank(a.feature) - statisticRank(b.feature);
  if (rankDiff !== 0) return rankDiff;

  return a.feature.localeCompare(b.feature);
};

/** The label shown for one filter value. */
export const filterValueLabel = (field, value) => {
  if (field === 'tag_type') return tagTypeLabel(value);
  if (value === null || value === undefined) return '';

  return featureLabel(value);
};

/**
 * Distinct values a field takes across the rows, as {value, text} options,
 * sorted by label. An amplitude of null is not offered: it means "not a
 * vibration measure", which the tag type filter already expresses.
 */
export const filterOptions = (rows, field) => {
  const seen = {};
  rows.forEach((row) => {
    const value = row[field];
    if (value === null || value === undefined || value === '') return;
    seen[value] = true;
  });

  return Object.keys(seen)
    .map(value => ({ value, text: filterValueLabel(field, value) }))
    .sort((a, b) => a.text.localeCompare(b.text));
};

/**
 * The rows that pass every selected filter and the search text.
 *
 * A filter with nothing selected passes everything; within one filter the
 * selected values are alternatives. The search matches the feature label,
 * the identifier and the tag type, so `rms`, `RMS velocity` and `vibration`
 * all find rms_velocity.
 */
export const applyFilters = (rows, filters, search) => {
  const term = (search || '').trim().toLowerCase();

  return rows.filter((row) => {
    const passesFilters = Object.keys(filters || {}).every((field) => {
      const selected = filters[field] || [];
      return !selected.length || selected.indexOf(row[field]) !== -1;
    });
    if (!passesFilters) return false;
    if (!term) return true;

    return [
      row.feature,
      featureLabel(row.feature),
      row.tag_type,
      tagTypeLabel(row.tag_type),
      row.amplitude_type ? featureLabel(row.amplitude_type) : ''
    ].some(text => String(text || '').toLowerCase().indexOf(term) !== -1);
  });
};

const SORT_LABELS = {
  tag_type: row => tagTypeLabel(row.tag_type),
  feature: row => featureLabel(row.feature),
  amplitude_type: row => (row.amplitude_type ? featureLabel(row.amplitude_type) : '')
};

/**
 * Rows ordered by the label a column shows, for the DataTable sort headers.
 *
 * Without a sort the curated order stands. Ties keep that order too, so a sort
 * by tag type still lists vibration's velocity family before acceleration.
 * An empty amplitude sorts last whichever way the column is ordered.
 */
export const sortRows = (rows, sort) => {
  const label = sort && SORT_LABELS[sort.name];
  if (!label) return rows;
  const direction = sort.order === 'desc' ? -1 : 1;

  return rows
    .map((row, index) => ({ row, index, text: label(row) }))
    .sort((a, b) => {
      if (!a.text !== !b.text) return a.text ? -1 : 1;
      const order = a.text.localeCompare(b.text) * direction;
      return order || a.index - b.index;
    })
    .map(entry => entry.row);
};
